import { memo, type MouseEvent, useCallback, useEffect } from 'react';

import { CardDate } from '../components/CardDate';
import { Category, CategoryId, CategoryPublication } from '../api/category';
import { CategoryPublicationCard } from './CategoryPublicationCard';
import { imageSrc } from '../api/media';
import { Option } from '../types/option';
import { useToggle } from '../hooks/useToggle';
import resetSVG from '../assets/reset.svg';
import Spinner from '../spinner/Spinner';

type Props = {
    category: Category;
    publications: Option<CategoryPublication[]>;
    loadingCategoryPublication: boolean;
    publicationsLoadError: Option<string>;
    handleUnarchive: (c: Category) => void;
    onLoadPublications: (id: CategoryId) => void;
    onRefreshPublications: (id: CategoryId) => void;
};

export const ArchiveCategoryCard = memo(function ArchiveCategoryCard({
    category,
    publications,
    loadingCategoryPublication,
    publicationsLoadError,
    handleUnarchive,
    onLoadPublications,
    onRefreshPublications,
}: Props) {
    const [isOpen, , handleToggle] = useToggle(false);

    useEffect(() => {
        if (isOpen) {
            onLoadPublications(category.id);
        }
    }, [isOpen, category.id, onLoadPublications]);

    const unarchiveCallback = useCallback(
        (event: MouseEvent<HTMLImageElement>) => {
            event.stopPropagation();
            handleUnarchive(category);
        },
        [handleUnarchive, category],
    );

    return (
        <li className="relative flex h-auto cursor-pointer flex-col overflow-hidden rounded-xl bg-white">
            <div className="flex items-center justify-between px-8 py-4" onClick={handleToggle}>
                <div className="flex items-center gap-2">
                    {category.media
                        .map(media => (
                            <img
                                src={imageSrc(media)}
                                alt={category.title}
                                className="h-[60px] w-[85px] rounded-[12px] object-cover"
                                loading="lazy"
                            />
                        ))
                        .toNull()}
                    <p className="my-[10px] text-medium font-medium text-dark-gray">
                        {category.title}
                    </p>
                </div>

                <div className="mr-[60px] flex justify-between gap-6">
                    <div className="flex-1">
                        <CardDate date={category.createdAt} />
                    </div>
                    <img
                        onClick={unarchiveCallback}
                        src={resetSVG}
                        className="w-6 cursor-pointer"
                        alt="Désarchiver"
                        role="button"
                    />
                    <button
                        type="button"
                        className="card-expanded"
                        aria-expanded={isOpen}
                        aria-label={isOpen ? 'Replier' : 'Déplier'}
                    />
                </div>
            </div>

            {isOpen && (
                <div className="flex flex-col items-center gap-2 bg-light-gray py-6 px-6">
                    {publicationsLoadError
                        .map(message => (
                            <p
                                className="w-full rounded-2xl bg-red-50 px-3 py-2 text-center text-small text-red-800"
                                role="alert"
                            >
                                {message}
                            </p>
                        ))
                        .toNull()}
                    {loadingCategoryPublication && (
                        <div className="relative h-[50px] w-[50px]">
                            <Spinner loading={true} />
                        </div>
                    )}
                    {!loadingCategoryPublication &&
                        publicationsLoadError.isEmpty() &&
                        publications
                            .map(items =>
                                items.length === 0 ? (
                                    <div key="empty" className="flex items-center justify-center">
                                        <p>Aucune publication</p>
                                    </div>
                                ) : (
                                    items.map(publication => (
                                        <CategoryPublicationCard
                                            key={publication.id}
                                            publication={publication}
                                            categoryId={category.id}
                                            onPublicationSaved={() =>
                                                onRefreshPublications(category.id)
                                            }
                                        />
                                    ))
                                ),
                            )
                            .toNull()}
                </div>
            )}
        </li>
    );
});
